// Orchestrates client-side GFPGAN face enhancement (auto-detect pipeline).
//   • decodes the image to RGB,
//   • the worker detects → aligns → restores → pastes back every face,
//   • re-encodes the full-resolution result to a PNG blob.
// No server, no upload — models download once and are cached in the browser.

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function canvasToPng(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/png');
    });
}

// RGBA (canvas) → tightly-packed RGB (what the worker/ops expect)
function rgbaToRgb(rgba, w, h) {
    const rgb = new Uint8Array(w * h * 3);
    for (let i = 0, j = 0; j < rgb.length; i += 4, j += 3) {
        rgb[j] = rgba[i]; rgb[j + 1] = rgba[i + 1]; rgb[j + 2] = rgba[i + 2];
    }
    return rgb;
}

function rgbToRgba(rgb, w, h) {
    const rgba = new Uint8ClampedArray(w * h * 4);
    for (let i = 0, j = 0; i < rgb.length; i += 3, j += 4) {
        rgba[j] = rgb[i]; rgba[j + 1] = rgb[i + 1]; rgba[j + 2] = rgb[i + 2]; rgba[j + 3] = 255;
    }
    return rgba;
}

function runWorker(rgb, width, height, onProgress, roi) {
    return new Promise((resolve, reject) => {
        const worker = new Worker(new URL('./faceEnhanceWorker.js', import.meta.url), { type: 'module' });
        worker.onmessage = (e) => {
            const { type, pct, stage, rgb: out, width: ow, height: oh, message } = e.data;
            if (type === 'progress') onProgress?.('download', pct);
            else if (type === 'stage') onProgress?.(stage, 0);
            else if (type === 'done') { worker.terminate(); resolve({ rgb: out, width: ow, height: oh }); }
            else if (type === 'error') { worker.terminate(); reject(new Error(message || 'failed')); }
        };
        worker.onerror = (err) => { worker.terminate(); reject(err); };
        worker.postMessage({ rgb, width, height, roi: roi || null }, [rgb.buffer]);
    });
}

/**
 * Auto-detect and enhance every face in an image, entirely in the browser.
 *
 * @param {string} imageSrc  object URL / data URL of the source image
 * @param {(stage:'download'|'detect'|'restore', pct:number)=>void} onProgress
 * @param {{x:number,y:number,w:number,h:number}|null} [roi]  optional face box (image px);
 *        when given, detection is constrained to that box (manual selection).
 * @returns {Promise<Blob>} PNG of the enhanced image at the original resolution
 * @throws {Error} with message 'NO_FACE' when no face is detected
 */
export async function runFaceEnhance(imageSrc, onProgress, roi = null) {
    const img = await loadImage(imageSrc);
    const w = img.naturalWidth;
    const h = img.naturalHeight;

    const inCanvas = document.createElement('canvas');
    inCanvas.width = w;
    inCanvas.height = h;
    const inCtx = inCanvas.getContext('2d', { willReadFrequently: true });
    inCtx.drawImage(img, 0, 0);
    const srcData = inCtx.getImageData(0, 0, w, h).data;
    const rgb = rgbaToRgb(srcData, w, h);

    const out = await runWorker(rgb, w, h, onProgress, roi);

    // Restore the original alpha channel so bg-removed (transparent) images stay
    // transparent instead of turning black. The worker only touches the opaque
    // face region and preserves the frame size, so the source alpha still aligns.
    const rgba = rgbToRgba(out.rgb, out.width, out.height);
    if (out.width === w && out.height === h) {
        for (let p = 3; p < rgba.length; p += 4) rgba[p] = srcData[p];
    }

    const outCanvas = document.createElement('canvas');
    outCanvas.width = out.width;
    outCanvas.height = out.height;
    outCanvas.getContext('2d').putImageData(new ImageData(rgba, out.width, out.height), 0, 0);
    return canvasToPng(outCanvas);
}
